/**
 * Transaction Manager - BIM 2025 Grade
 * 
 * Robust transaction handling with groups, sub-transactions,
 * rollback support, and regeneration control.
 * 
 * UPGRADE: Now uses Immer for Delta-based state storage (Patches).
 */

import { v4 as uuidv4 } from 'uuid';
import { BIMElement, Project } from './types';
import { produce, enablePatches, applyPatches, Patch, enableMapSet } from 'immer';
import { GeometricConstraintSolver } from './GeometricConstraintSolver';
import { failureResolutionService } from './FailureResolutionService';

// Enable Immer plugins
enablePatches();
enableMapSet();

// ==================== Transaction Types ====================

export type TransactionStatus =
    | 'NotStarted'
    | 'Started'
    | 'Committed'
    | 'RolledBack'
    | 'Pending';

export interface TransactionChange {
    type: 'ADD' | 'MODIFY' | 'DELETE';
    elementId: string;
    // For ADD: new state is needed. Inverse is DELETE.
    // For DELETE: old state is needed (to restore). Inverse is ADD.
    // For MODIFY: Patches are used.
    patches?: Patch[];
    inversePatches?: Patch[];

    // Fallback/Legacy (optional used for ADD/DELETE restoration)
    fullState?: BIMElement;

    timestamp: number;
}

export interface SubTransaction {
    id: string;
    name: string;
    changes: TransactionChange[];
    status: TransactionStatus;
    startTime: number;
    endTime?: number;
}

export interface Transaction {
    id: string;
    name: string;
    status: TransactionStatus;
    subTransactions: SubTransaction[];
    changes: TransactionChange[];
    startTime: number;
    endTime?: number;
    autoRegenerate: boolean;
}

export interface TransactionGroup {
    id: string;
    name: string;
    transactions: Transaction[];
    status: TransactionStatus;
    assimilate: boolean;
}

export type RegenerationMode = 'Automatic' | 'Manual' | 'Continuous';

export interface RegenerationOptions {
    mode: RegenerationMode;
    affectedElementsOnly: boolean;
    recalculateViews: boolean;
    updateSchedules: boolean;
}

// ==================== Transaction Manager ====================

export class TransactionManager {
    private project: Project | null = null;
    private activeTransaction: Transaction | null = null;
    private activeSubTransaction: SubTransaction | null = null;
    private groupStack: TransactionGroup[] = [];

    private undoStack: Transaction[] = [];
    private redoStack: Transaction[] = [];

    private regenerationOptions: RegenerationOptions = {
        mode: 'Automatic',
        affectedElementsOnly: true,
        recalculateViews: true,
        updateSchedules: true
    };

    private listeners: Set<(event: TransactionEvent) => void> = new Set();
    private updaters: Set<IUpdater> = new Set();

    // ==================== Project Binding ====================

    setProject(project: Project): void {
        this.project = project;
    }

    getProject(): Project | null {
        return this.project;
    }

    // ==================== IUpdater (Dynamic Updates) ====================

    registerUpdater(updater: IUpdater): void {
        this.updaters.add(updater);
    }

    unregisterUpdater(updater: IUpdater): void {
        this.updaters.delete(updater);
    }

    private executeUpdaters(transaction: Transaction): void {
        const maxIterations = 10;
        let iteration = 0;
        let changesOccurred = true;

        while (changesOccurred && iteration < maxIterations) {
            changesOccurred = false;
            iteration++;

            const changedElementIds = new Set<string>();
            transaction.changes.forEach(c => changedElementIds.add(c.elementId));

            if (changedElementIds.size === 0) break;

            for (const updater of this.updaters) {
                if (updater.execute(this.project!, changedElementIds)) {
                    changesOccurred = true;
                }
            }
        }
    }

    // ==================== Transaction Group ====================

    startTransactionGroup(name: string, assimilate: boolean = true): TransactionGroup {
        const newGroup: TransactionGroup = {
            id: uuidv4(),
            name,
            transactions: [],
            status: 'Started',
            assimilate
        };

        this.groupStack.push(newGroup);
        this.emit({ type: 'GROUP_STARTED', group: newGroup });
        return newGroup;
    }

    commitTransactionGroup(): void {
        if (this.groupStack.length === 0) throw new Error('No active transaction group');
        const groupToCommit = this.groupStack.pop()!;

        if (this.activeTransaction && this.activeTransaction.status === 'Started') {
            throw new Error(`Cannot commit group '${groupToCommit.name}': Transaction '${this.activeTransaction.name}' is still open.`);
        }

        groupToCommit.status = 'Committed';
        const parentGroup = this.groupStack.length > 0 ? this.groupStack[this.groupStack.length - 1] : null;

        if (groupToCommit.assimilate) {
            // Merge transactions
            if (groupToCommit.transactions.length > 0) {
                const mergedTransaction: Transaction = {
                    id: uuidv4(),
                    name: groupToCommit.name,
                    status: 'Committed',
                    subTransactions: [],
                    changes: [],
                    startTime: groupToCommit.transactions[0].startTime,
                    endTime: Date.now(),
                    autoRegenerate: true
                };

                for (const tx of groupToCommit.transactions) {
                    mergedTransaction.changes.push(...tx.changes);
                    for (const sub of tx.subTransactions) {
                        mergedTransaction.changes.push(...sub.changes);
                    }
                }

                if (parentGroup) {
                    parentGroup.transactions.push(mergedTransaction);
                } else {
                    this.undoStack.push(mergedTransaction);
                    this.redoStack = [];
                }
            }
        } else {
            if (parentGroup) {
                parentGroup.transactions.push(...groupToCommit.transactions);
            } else {
                this.undoStack.push(...groupToCommit.transactions);
                this.redoStack = [];
            }
        }

        this.emit({ type: 'GROUP_COMMITTED', group: groupToCommit });
    }

    rollbackTransactionGroup(): void {
        if (this.groupStack.length === 0) throw new Error('No active transaction group');
        const groupToRollback = this.groupStack.pop()!;

        if (this.activeTransaction && this.activeTransaction.status === 'Started') {
            this.rollbackTransaction();
        }

        for (let i = groupToRollback.transactions.length - 1; i >= 0; i--) {
            const tx = groupToRollback.transactions[i];
            if (tx.status === 'Committed') {
                this.rollbackTransactionChanges(tx);
            }
        }

        groupToRollback.status = 'RolledBack';
        this.emit({ type: 'GROUP_ROLLED_BACK', group: groupToRollback });
    }

    // ==================== Transaction ====================

    startTransaction(name: string, autoRegenerate: boolean = true): Transaction {
        if (this.activeTransaction && this.activeTransaction.status === 'Started') {
            throw new Error('Transaction already active. Commit or rollback first.');
        }

        this.activeTransaction = {
            id: uuidv4(),
            name,
            status: 'Started',
            subTransactions: [],
            changes: [],
            startTime: Date.now(),
            autoRegenerate
        };

        const activeGroup = this.groupStack.length > 0 ? this.groupStack[this.groupStack.length - 1] : null;
        if (activeGroup) {
            activeGroup.transactions.push(this.activeTransaction);
        }

        this.emit({ type: 'TRANSACTION_STARTED', transaction: this.activeTransaction });
        return this.activeTransaction;
    }

    commitTransaction(): void {
        if (!this.activeTransaction || this.activeTransaction.status !== 'Started') {
            throw new Error('No active started transaction');
        }
        if (this.activeSubTransaction && this.activeSubTransaction.status === 'Started') {
            throw new Error('Cannot commit transaction while sub-transaction is active');
        }

        this.executeUpdaters(this.activeTransaction);

        this.activeTransaction.status = 'Committed';
        this.activeTransaction.endTime = Date.now();

        if (this.groupStack.length === 0) {
            // Check constraints only on top-level transactions
            const validationError = this.checkConstraints(this.activeTransaction);
            if (validationError) {
                failureResolutionService.registerFailure({
                    id: uuidv4(),
                    severity: 'Error',
                    description: `Constraint Violation: ${validationError}`,
                    resolutionOptions: ['Cancel', 'DetachConstraints']
                });
                failureResolutionService.postFailure('last-failure');

                if (failureResolutionService.hasErrors()) {
                    console.warn('Transaction committed with constraint violations:', validationError);
                }
            }

            this.undoStack.push(this.activeTransaction);
            this.redoStack = [];
        }

        if (this.activeTransaction.autoRegenerate && this.regenerationOptions.mode === 'Automatic') {
            this.regenerate();
        }

        this.emit({ type: 'TRANSACTION_COMMITTED', transaction: this.activeTransaction });
        this.activeTransaction = null;
    }

    private checkConstraints(transaction: Transaction): string | null {
        if (!this.project) return null;

        let hasConstraints = false;

        // 1. Collect elements involved
        const elementsToCheck = new Set<string>();
        transaction.changes.forEach(change => elementsToCheck.add(change.elementId));

        elementsToCheck.forEach(elId => {
            const el = this.project!.elements.get(elId);
            if (el && el.constraints) {
                el.constraints.forEach(c => {
                    // Basic mapping logic
                    if ((c as any).type === 'DIMENSIONAL') {
                        // Placeholder: Logic to extract points and add to solver
                        // e.g. solver.addConstraint(...) 
                        hasConstraints = true;
                    }
                });
            }
        });

        if (hasConstraints) {
            // In full implementation:
            // const converged = solver.solve();
            // if (!converged) return "Constraints could not be satisfied";
        }

        return null;
    }

    rollbackTransaction(): void {
        if (!this.activeTransaction) throw new Error('No active transaction');

        for (let i = this.activeTransaction.subTransactions.length - 1; i >= 0; i--) {
            const sub = this.activeTransaction.subTransactions[i];
            if (sub.status === 'Started' || sub.status === 'Committed') {
                this.rollbackSubTransactionChanges(sub);
            }
        }

        this.rollbackTransactionChanges(this.activeTransaction);

        this.activeTransaction.status = 'RolledBack';
        this.activeTransaction.endTime = Date.now();

        this.emit({ type: 'TRANSACTION_ROLLED_BACK', transaction: this.activeTransaction });
        this.activeSubTransaction = null;
        this.activeTransaction = null;
    }

    // ==================== Sub-Transaction ====================

    startSubTransaction(name: string): SubTransaction {
        if (!this.activeTransaction || this.activeTransaction.status !== 'Started') {
            throw new Error('No active transaction');
        }
        if (this.activeSubTransaction && this.activeSubTransaction.status === 'Started') {
            throw new Error('Sub-transaction already active');
        }

        this.activeSubTransaction = {
            id: uuidv4(),
            name,
            changes: [],
            status: 'Started',
            startTime: Date.now()
        };

        this.activeTransaction.subTransactions.push(this.activeSubTransaction);
        this.emit({ type: 'SUB_TRANSACTION_STARTED', subTransaction: this.activeSubTransaction });
        return this.activeSubTransaction;
    }

    commitSubTransaction(): void {
        if (!this.activeSubTransaction) throw new Error('No active sub-transaction');
        this.activeSubTransaction.status = 'Committed';
        this.activeSubTransaction.endTime = Date.now();
        this.emit({ type: 'SUB_TRANSACTION_COMMITTED', subTransaction: this.activeSubTransaction });
        this.activeSubTransaction = null;
    }

    rollbackSubTransaction(): void {
        if (!this.activeSubTransaction) throw new Error('No active sub-transaction');
        this.rollbackSubTransactionChanges(this.activeSubTransaction);
        this.activeSubTransaction.status = 'RolledBack';
        this.activeSubTransaction.endTime = Date.now();
        this.emit({ type: 'SUB_TRANSACTION_ROLLED_BACK', subTransaction: this.activeSubTransaction });
        this.activeSubTransaction = null;
    }

    // ==================== Change Recording (Immer / Delta) ====================

    recordAdd(element: BIMElement): void {
        this.recordChange({
            type: 'ADD',
            elementId: element.id,
            fullState: this.cloneElement(element), // Needed for 'Redo' or 'Undo' of Delete
            timestamp: Date.now()
        });
    }

    recordDelete(element: BIMElement): void {
        this.recordChange({
            type: 'DELETE',
            elementId: element.id,
            fullState: this.cloneElement(element), // Needed for 'Undo' (restore)
            timestamp: Date.now()
        });
    }

    /**
     * Record a modification by generating patches between old and new state.
     */
    recordModify(elementId: string, previousState: BIMElement, newState: BIMElement): void {
        // Generate patches
        let finalPatches: Patch[] = [];
        let finalInversePatches: Patch[] = [];

        // Use produce to calculate diff
        produce(previousState, (draft) => {
            // Apply new state to draft to find differences
            Object.assign(draft, newState);
            // Note: Object.assign might not be deep enough for nested changes if references match.
        }, (patches, inversePatches) => {
            finalPatches = patches;
            finalInversePatches = inversePatches;
        });

        this.recordChange({
            type: 'MODIFY',
            elementId,
            patches: finalPatches,
            inversePatches: finalInversePatches,
            timestamp: Date.now()
        });
    }

    private recordChange(change: TransactionChange): void {
        if (!this.activeTransaction || this.activeTransaction.status !== 'Started') {
            throw new Error('No active transaction to record change');
        }

        if (this.activeSubTransaction && this.activeSubTransaction.status === 'Started') {
            this.activeSubTransaction.changes.push(change);
        } else {
            this.activeTransaction.changes.push(change);
        }

        this.emit({ type: 'CHANGE_RECORDED', change });
    }

    // ==================== Undo/Redo Logic ====================

    private rollbackTransactionChanges(tx: Transaction): void {
        for (let i = tx.changes.length - 1; i >= 0; i--) {
            this.reverseChange(tx.changes[i]);
        }
    }

    private rollbackSubTransactionChanges(sub: SubTransaction): void {
        for (let i = sub.changes.length - 1; i >= 0; i--) {
            this.reverseChange(sub.changes[i]);
        }
    }

    private reverseChange(change: TransactionChange): void {
        if (!this.project) return;

        switch (change.type) {
            case 'ADD':
                // Undo Add = Delete
                this.project.elements.delete(change.elementId);
                break;
            case 'DELETE':
                // Undo Delete = Restore
                if (change.fullState) {
                    this.project.elements.set(change.elementId, change.fullState);
                }
                break;
            case 'MODIFY':
                // Undo Modify = Apply Inverse Patches
                if (change.inversePatches && change.inversePatches.length > 0) {
                    const current = this.project.elements.get(change.elementId);
                    if (current) {
                        const restored = applyPatches(current, change.inversePatches);
                        this.project.elements.set(change.elementId, restored);
                    }
                }
                break;
        }
    }

    private applyChange(change: TransactionChange): void {
        if (!this.project) return;

        switch (change.type) {
            case 'ADD':
                if (change.fullState) {
                    this.project.elements.set(change.elementId, change.fullState);
                }
                break;
            case 'DELETE':
                this.project.elements.delete(change.elementId);
                break;
            case 'MODIFY':
                if (change.patches && change.patches.length > 0) {
                    const current = this.project.elements.get(change.elementId);
                    if (current) {
                        const updated = applyPatches(current, change.patches);
                        this.project.elements.set(change.elementId, updated);
                    }
                }
                break;
        }
    }

    undo(): boolean {
        if (this.undoStack.length === 0) return false;
        const tx = this.undoStack.pop()!;

        this.rollbackTransactionChanges(tx);
        for (let i = tx.subTransactions.length - 1; i >= 0; i--) {
            this.rollbackSubTransactionChanges(tx.subTransactions[i]);
        }

        this.redoStack.push(tx);
        this.emit({ type: 'UNDO', transaction: tx });

        if (this.regenerationOptions.mode === 'Automatic') this.regenerate();
        return true;
    }

    redo(): boolean {
        if (this.redoStack.length === 0) return false;
        const tx = this.redoStack.pop()!;

        for (const change of tx.changes) this.applyChange(change);
        for (const sub of tx.subTransactions) {
            for (const change of sub.changes) this.applyChange(change);
        }

        this.undoStack.push(tx);
        this.emit({ type: 'REDO', transaction: tx });

        if (this.regenerationOptions.mode === 'Automatic') this.regenerate();
        return true;
    }

    // ==================== Utilities ====================

    canUndo(): boolean { return this.undoStack.length > 0; }
    canRedo(): boolean { return this.redoStack.length > 0; }
    getUndoName(): string | null { return this.undoStack.length > 0 ? this.undoStack[this.undoStack.length - 1].name : null; }
    getRedoName(): string | null { return this.redoStack.length > 0 ? this.redoStack[this.redoStack.length - 1].name : null; }

    setRegenerationOptions(options: Partial<RegenerationOptions>): void {
        this.regenerationOptions = { ...this.regenerationOptions, ...options };
    }

    regenerate(): void {
        this.emit({ type: 'REGENERATION_STARTED' });
        // Trigger generic update (broadcasting to other systems)
        this.emit({ type: 'REGENERATION_COMPLETED' });
    }

    hasActiveTransaction(): boolean { return this.activeTransaction !== null && this.activeTransaction.status === 'Started'; }
    hasActiveGroup(): boolean { return this.groupStack.length > 0 && this.groupStack[this.groupStack.length - 1].status === 'Started'; }

    private cloneElement(element: BIMElement): BIMElement {
        // Fallback deep clone for ADD/DELETE snapshots
        return structuredClone(element);
    }

    // ==================== Events ====================
    private emit(event: TransactionEvent): void {
        this.listeners.forEach(listener => listener(event));
    }

    subscribe(listener: (event: TransactionEvent) => void): () => void {
        this.listeners.add(listener);
        return () => this.listeners.delete(listener);
    }
}

export interface IUpdater {
    execute(project: Project, changedElementIds: Set<string>): boolean;
}

export type TransactionEvent =
    | { type: 'GROUP_STARTED'; group: TransactionGroup }
    | { type: 'GROUP_COMMITTED'; group: TransactionGroup }
    | { type: 'GROUP_ROLLED_BACK'; group: TransactionGroup }
    | { type: 'TRANSACTION_STARTED'; transaction: Transaction }
    | { type: 'TRANSACTION_COMMITTED'; transaction: Transaction }
    | { type: 'TRANSACTION_ROLLED_BACK'; transaction: Transaction }
    | { type: 'SUB_TRANSACTION_STARTED'; subTransaction: SubTransaction }
    | { type: 'SUB_TRANSACTION_COMMITTED'; subTransaction: SubTransaction }
    | { type: 'SUB_TRANSACTION_ROLLED_BACK'; subTransaction: SubTransaction }
    | { type: 'CHANGE_RECORDED'; change: TransactionChange }
    | { type: 'UNDO'; transaction: Transaction }
    | { type: 'REDO'; transaction: Transaction }
    | { type: 'REGENERATION_STARTED' }
    | { type: 'REGENERATION_COMPLETED' };

export const transactionManager = new TransactionManager();
